import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { API, createTranslator } from "@/lib/constants";
import { getAdminHeaders, setAdminSessionToken } from "@/lib/admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AdminLoginPage({ language }) {
  const t = createTranslator(language);
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/admin/status`, {
          headers: getAdminHeaders(),
        });
        if (response.data?.authenticated) {
          navigate("/admin-dashboard");
        }
      } catch {
        // Expected: unauthenticated users get 401
      }
    };
    checkStatus();
  }, [navigate]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password }, { timeout: 10000 });
      if (response.data?.session_token) {
        setAdminSessionToken(response.data.session_token);
      }
      navigate("/admin-dashboard");
    } catch {
      setError(t("adminLogin.error", "Invalid password. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-[#f4f6fb] ${language === "hi" ? "font-hindi" : ""}`}>
      <Header language={language} />
      <main className="pt-28 pb-20">
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between gap-8 items-start">
            <div>
              <p
                className="text-xs tracking-[0.3em] uppercase text-[#c2873c]"
                data-testid="admin-login-eyebrow"
              >
                {t("adminLogin.eyebrow", "Admin")}
              </p>
              <h1
                className="font-heading text-3xl sm:text-4xl text-[#003366] mt-4"
                data-testid="admin-login-title"
              >
                {t("adminLogin.title", "Admin Login")}
              </h1>
            </div>
            <p className="max-w-md text-slate-600" data-testid="admin-login-description">
              {t(
                "adminLogin.description",
                "Enter the admin password to access confidential submissions."
              )}
            </p>
          </div>

          <form
            className="mt-6 bg-white border border-slate-200 shadow-sm p-6"
            onSubmit={handleLogin}
            data-testid="admin-login-form"
          >
            <label
              className="block text-sm font-semibold text-[#003366]"
              htmlFor="admin-password"
              data-testid="admin-login-password-label"
            >
              {t("adminLogin.passwordLabel", "Admin Password")}
            </label>
            <Input
              id="admin-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t("adminLogin.passwordPlaceholder", "Enter admin password")}
              className="rounded-none mt-3"
              data-testid="admin-login-password-input"
            />
            {error && (
              <p className="mt-3 text-sm text-red-600" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <p className="mt-3 text-xs text-slate-500" data-testid="admin-login-helper">
              {t("adminLogin.helper", "Authorized access only.")}
            </p>
            <Button
              type="submit"
              className="mt-6 rounded-none bg-[#003366] text-white hover:bg-[#002244]"
              disabled={isLoading}
              data-testid="admin-login-submit"
            >
              {isLoading
                ? t("adminLogin.loading", "Signing in...")
                : t("adminLogin.submit", "Login")}
            </Button>
          </form>
        </section>
      </main>
      <Footer language={language} />
    </div>
  );
}
